import { useMemo, useState } from "react";
import useSWR from "swr";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { layerIcon } from "commons/cdn";

interface LayerIconPickerProps {
  currentLayerIconId?: string | null;
  children: React.ReactNode;
  disabled?: boolean;
  onSelect: (layerIcon: any) => void;
}

export function LayerIconPicker({
  currentLayerIconId,
  children,
  disabled,
  onSelect,
}: LayerIconPickerProps) {
  const [selected, setSelected] = useState<any>(
    currentLayerIconId ? { id: currentLayerIconId } : null
  );
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);

  // 获取 layericon 列表
  const { data: layerIcons = [], isLoading } = useSWR([
    "/layericons",
    { author: "admin", limit: 1000 },
  ]);

  // 根据关键词过滤
  const filteredIcons = useMemo(() => {
    if (!keyword.trim()) {
      return layerIcons;
    }
    return layerIcons.filter(
      (icon: any) =>
        icon.name?.toLowerCase().includes(keyword.toLowerCase()) ||
        icon.id?.toLowerCase().includes(keyword.toLowerCase())
    );
  }, [keyword, layerIcons]);

  const handleSubmit = () => {
    if (selected) {
      onSelect(selected);
      setOpen(false);
    }
  };

  return (
    <Dialog open={open && !disabled} onOpenChange={(val) => !disabled && setOpen(val)}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>选择 LayerIcon</DialogTitle>
          <DialogDescription className="hidden" />
        </DialogHeader>

        {/* 搜索框 */}
        <div className="flex gap-2">
          <Input
            placeholder="搜索 LayerIcon 名称或 ID..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="flex-1"
          />
          {keyword && (
            <Button
              variant="ghost"
              onClick={() => setKeyword("")}
              className="shrink-0"
            >
              清除
            </Button>
          )}
        </div>

        {/* LayerIcon 列表 */}
        <ScrollArea className="flex-1 max-h-[50vh]">
          {isLoading ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              加载中...
            </div>
          ) : filteredIcons.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              暂无数据
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-3 p-2">
              {filteredIcons.map((icon: any) => (
                <LayerIconItem
                  key={icon.id}
                  icon={icon}
                  selected={selected?.id === icon.id}
                  onClick={() => setSelected(icon)}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* 底部操作栏 */}
        <DialogFooter className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {selected ? (
              <>
                已选择: <span className="font-medium">{selected.name || selected.id}</span>
              </>
            ) : (
              "请选择一个 LayerIcon"
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button onClick={handleSubmit} disabled={!selected}>
              确定
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface LayerIconItemProps {
  icon: any;
  selected: boolean;
  onClick: () => void;
}

function LayerIconItem({ icon, selected, onClick }: LayerIconItemProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-1 p-2 rounded-lg border-2 cursor-pointer transition-all",
        "hover:border-primary hover:shadow-md",
        selected
          ? "border-primary bg-primary/10 ring-2 ring-primary/30"
          : "border-transparent bg-muted/30"
      )}
      onClick={onClick}
    >
      <div className="w-full aspect-square bg-white rounded-md overflow-hidden flex items-center justify-center">
        <img
          src={layerIcon(icon.id)}
          alt={icon.name || icon.id}
          className="w-full h-full object-contain"
        />
      </div>
      <div className="text-xs text-center truncate w-full" title={icon.name || icon.id}>
        {icon.name || icon.id}
      </div>
    </div>
  );
}

